import { uiText } from '../i18n/uiText';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authApi, resetApiSession } from '../api';
import { resetAuditStore } from './auditsStore';
import { resetWorkspaceStores } from './workspaceStore';

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistUser(user) {
  try {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else localStorage.removeItem('user');
  } catch { /* HttpOnly cookie and in-memory state remain authoritative. */ }
}

function clearAuthSession() {
  persistUser(null);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const userRef = useRef(user);
  const [loading, setLoading] = useState(true);
  const authRevision = useRef(0);
  const refreshSequence = useRef(0);
  const mountedRef = useRef(true);
  const authOperation = useRef(false);
  const initialCheckPending = useRef(true);

  const beginSessionChange = useCallback(() => {
    authRevision.current += 1;
    refreshSequence.current += 1;
    resetApiSession();
  }, []);

  const setAuthenticatedUser = useCallback((nextUser) => {
    const previousIdentity = userRef.current ? `${userRef.current.id}:${userRef.current.role}` : '';
    const nextIdentity = nextUser ? `${nextUser.id}:${nextUser.role}` : '';
    if (previousIdentity !== nextIdentity) {
      beginSessionChange();
      resetAuditStore();
      resetWorkspaceStores();
    }
    if (nextUser) persistUser(nextUser);
    else clearAuthSession();
    userRef.current = nextUser || null;
    setUser(nextUser || null);
  }, [beginSessionChange]);

  const refresh = useCallback(async () => {
    if (authOperation.current) return userRef.current;
    const revision = authRevision.current;
    const sequence = ++refreshSequence.current;
    try {
      const res = await authApi.me();
      if (!mountedRef.current || revision !== authRevision.current || sequence !== refreshSequence.current) return userRef.current;
      const nextUser = res?.data || null;
      setAuthenticatedUser(nextUser);
      return nextUser;
    } catch (error) {
      if (!mountedRef.current || revision !== authRevision.current || sequence !== refreshSequence.current || error?.code === 'ERR_CANCELED') return userRef.current;
      throw error;
    }
  }, [setAuthenticatedUser]);

  useEffect(() => {
    let mounted = true;
    mountedRef.current = true;
    initialCheckPending.current = true;

    refresh()
      .catch(() => {
        if (!mounted) return;
        setAuthenticatedUser(null);
      })
      .finally(() => {
        if (mounted) {
          initialCheckPending.current = false;
          setLoading(false);
        }
      });

    const handleForcedLogout = () => {
      initialCheckPending.current = false;
      setAuthenticatedUser(null);
      setLoading(false);
    };

    window.addEventListener('app:auth-required', handleForcedLogout);
    return () => {
      mounted = false;
      mountedRef.current = false;
      window.removeEventListener('app:auth-required', handleForcedLogout);
    };
  }, [refresh, setAuthenticatedUser]);

  useEffect(() => {
    let refreshInFlight = false;

    const syncFromServer = async () => {
      // pageshow/focus can fire during the first /auth/me request. Starting a
      // newer refresh then invalidates that response while its finally clears
      // loading, briefly redirecting a valid session to the login page.
      if (initialCheckPending.current || refreshInFlight || document.visibilityState === 'hidden') return;
      refreshInFlight = true;
      try {
        await refresh();
      } catch (error) {
        // A temporary network/5xx failure is not proof of an expired session.
        // Unmounting the editor here used to discard the current form on focus.
        if ([401, 403].includes(error?.response?.status)) setAuthenticatedUser(null);
      } finally {
        refreshInFlight = false;
      }
    };

    const syncFromStorage = (event) => {
      if (event.key !== 'user') return;
      try {
        setAuthenticatedUser(event.newValue ? JSON.parse(event.newValue) : null);
      } catch {
        setAuthenticatedUser(null);
      }
      // The HttpOnly session cookie is shared by all tabs. Confirm the role
      // against the server instead of trusting a stale localStorage snapshot.
      syncFromServer();
    };

    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') syncFromServer();
    };

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', syncFromServer);
    window.addEventListener('pageshow', syncFromServer);
    window.addEventListener('app:auth-refresh', syncFromServer);
    document.addEventListener('visibilitychange', syncWhenVisible);
    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', syncFromServer);
      window.removeEventListener('pageshow', syncFromServer);
      window.removeEventListener('app:auth-refresh', syncFromServer);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [refresh, setAuthenticatedUser]);

  const login = async (username, password) => {
    if (authOperation.current) throw new Error(uiText("Дождитесь завершения предыдущего входа или выхода."));
    authOperation.current = true;
    beginSessionChange();
    setLoading(true);
    try {
      const res = await authApi.login(username, password);
      const nextUser = res?.data?.user;
      if (!nextUser) throw new Error(uiText("Сервер вернул неполные данные авторизации"));
      setAuthenticatedUser(nextUser);
      return nextUser;
    } catch (error) {
      setAuthenticatedUser(null);
      throw error;
    } finally {
      authOperation.current = false;
      setLoading(false);
    }
  };

  const logout = async () => {
    if (authOperation.current) return;
    authOperation.current = true;
    beginSessionChange();
    try {
      await authApi.logout();
    } catch {
      // ignore network errors during logout
    }
    setAuthenticatedUser(null);
    setLoading(false);
    authOperation.current = false;
  };

  const value = useMemo(
    () => ({ user, login, logout, refresh, setUser: setAuthenticatedUser, loading }),
    [user, loading, refresh, setAuthenticatedUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
